import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  BarChart2,
  LayoutDashboard,
  Menu,
  MonitorSmartphone,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import { usePermissions } from './PermissionsContext'
import { availableModules } from './modules'
import { Sidebar } from './Sidebar'
import { UserMenu } from './UserMenu'
import { HelpButton } from './HelpButton'
import { AttentionBadge } from '../features/attention/AttentionBadge'
import { AlertsBadge } from '../features/alerts/AlertsBadge'
import { BrandSwitcher } from './BrandSwitcher'
import { ThemeSwitcher } from '../app/ThemeSwitcher'
import { cn } from '../design/cn'
import { BrandMark } from '../design/BrandMark'
import { CommandPalette } from './CommandPalette'

const ICON_MAP: Record<string, LucideIcon> = {
  MonitorSmartphone,
  BarChart2,
  LayoutDashboard,
  Settings,
  ShieldCheck,
}

function ModuleTab({
  basePath,
  displayName,
  icon,
}: {
  basePath: string
  displayName: string
  icon: string
}) {
  const Icon = ICON_MAP[icon] ?? Settings
  return (
    <NavLink
      to={basePath}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-xs rounded-md px-md py-sm text-sm transition-colors',
          isActive
            ? 'bg-header-hover text-header-fg font-medium'
            : 'text-header-muted hover:bg-header-hover hover:text-header-fg',
        )
      }
    >
      <Icon size={15} aria-hidden="true" />
      {displayName}
    </NavLink>
  )
}

/**
 * The portal chrome: a module bar, brand and user controls, and an outlet the active module
 * renders into.
 *
 * Every colour is a token. The shell this is modelled on hardcoded `bg-blue-700`, `bg-gray-50` and
 * `text-white/70`, which is why its header ignored the theme entirely; here those classes would
 * not even compile.
 */
export function AppShell() {
  const { hasPermission, isLoading } = usePermissions()
  const modules = availableModules().filter((m) => hasPermission(m.permissionKey))

  // Below the `lg` breakpoint the sidebar is a drawer: hidden until the menu button opens it, and
  // closed again by choosing a destination, by the backdrop, or by Escape. At a phone's 390px a
  // fixed 240px sidebar left 150px for the page, and the header alone was wider than the screen.
  const [navOpen, setNavOpen] = useState(false)
  const { pathname } = useLocation()
  useEffect(() => setNavOpen(false), [pathname])
  useEffect(() => {
    if (!navOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen])

  return (
    <div className="flex h-screen flex-col bg-bg-content">
      <header
        className="flex h-header shrink-0 items-center gap-sm bg-header-bg px-md shadow-sm sm:px-lg"
        style={{ zIndex: 'var(--z-header)' }}
      >
        {/*
          The product lockup, as the Blazor header has it. A bare "Relay" wordmark named the
          module rather than the product, and sat next to a module tab that repeated it.
        */}
        <button
          type="button"
          onClick={() => setNavOpen((open) => !open)}
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          aria-controls="relay-sidebar"
          className="-ml-xs shrink-0 rounded-md p-xs text-header-muted transition-colors hover:bg-header-hover hover:text-header-fg lg:hidden"
        >
          <Menu size={20} aria-hidden="true" />
        </button>
        <span className="flex shrink-0 items-center gap-sm text-md font-semibold tracking-tight text-header-fg sm:mr-md">
          <BrandMark size={22} className="text-accent" />
          {/* The mark alone on a phone, where the words would push the controls off the screen. */}
          <span className="hidden sm:inline">Device Management</span>
        </span>

        {/*
          The module bar appears only when there is somewhere else to go. With Relay alone it was a
          single tab labelled "Devices" beside a sidebar whose first entries are Dashboard and
          Devices — two navigations for one destination, and the smaller of them meaningless.
        */}
        {!isLoading && modules.length > 1 ? (
          <nav aria-label="Modules" className="flex flex-1 items-center gap-xs overflow-x-auto">
            {modules.map((m) => (
              <ModuleTab key={m.name} {...m} />
            ))}
          </nav>
        ) : (
          // min-w-0: the search box gives up width before the badges and the account menu do.
          <div className="flex min-w-0 flex-1 justify-center px-sm">
            <CommandPalette />
          </div>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-xs sm:ml-sm sm:gap-sm">
          <AlertsBadge />
          <AttentionBadge />
          <HelpButton />
          <ThemeSwitcher />
          <BrandSwitcher />
          <UserMenu />
        </div>
      </header>

      {/*
        The module's navigation sits beside its content, as the Blazor layout does. It belongs to
        the shell rather than to RelayRoutes because RelayRoutes is deliberately chrome-free: it
        also renders inside the AboveStore shell, which supplies navigation of its own.
      */}
      <div className="relative flex min-h-0 flex-1">
        {navOpen ? (
          <div
            aria-hidden="true"
            data-nav-backdrop=""
            onClick={() => setNavOpen(false)}
            className="absolute inset-0 z-sidebar bg-overlay lg:hidden"
          />
        ) : null}
        <div
          id="relay-sidebar"
          className={cn(
            'absolute inset-y-0 left-0 z-sidebar shadow-lg lg:static lg:z-auto lg:flex lg:shadow-none',
            navOpen ? 'flex' : 'hidden',
          )}
        >
          <Sidebar />
        </div>
        {/* min-w-0, so a wide table scrolls inside its own container instead of widening the page. */}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
